/**
 * The tap a card-level action button sends — the pure half of `CardActions.tsx`.
 *
 * Design doc §11.4/§11.5: a card's `actions`/`links` are the SAME `Action`/
 * `Link` models `ObjectCard` already carried, now on every card the backend
 * builds them for. The value is the load-bearing half — `docs/graphs/peterhof_guide.md`'s
 * "Visitor-voice text (chip values) is load-bearing", the same invariant
 * `Chips.tsx` holds for the board's own `Button`s.
 *
 * **No fallback to `label`, ever.** `ObjectCard.tsx:88` used to send
 * `action.value ?? action.label` — a bypass of the tap bridge found on design
 * review (§11.1): the catalogue schema makes `value` required, so a fallback
 * can only ever paper over a real defect (a caption sent to the router in
 * place of the sentence it expects) rather than handle a legal state.
 */

import type { TapPayload } from '../a2ui/actions';

export type CardAction = {
  label: string;
  /** LOAD-BEARING: sent byte for byte as the visitor's next message. */
  value: string;
  emoji?: string;
};

export type CardLink = {
  label: string;
  url: string;
};

/**
 * Builds the payload `dispatchTap` sends for one card action button.
 *
 * Deliberately does NOT go through `a2ui/actions.ts::readAction` — that
 * reader parses an INCOMING A2UI action object (`{event:{userMessage}}`) and
 * its first guard rejects anything without an `event` record, so calling it
 * on a card's `{label, value, emoji}` prop returns `null` for every tap,
 * silently (found on design review, §11.4). `readAction`'s one real job —
 * refusing a `userMessage` that arrived as a DynamicString PATH rather than a
 * literal — does not apply here either: `value` is a plain string in the
 * generated zod, validated in the reducer before it ever left the backend.
 *
 * So the envelope is synthesised directly, with a REAL `event.name` —
 * `'card_action'`, never the `dispatchTap` default of `'tap'` — so
 * `context.a2ui_action.name` can tell a card tap from a chip tap once the
 * graph starts reading it.
 */
export function cardActionTap(action: CardAction): TapPayload {
  return {
    userMessage: action.value,
    action: {
      event: { name: 'card_action', userMessage: action.value },
      label: action.label,
      ...(action.emoji ? { emoji: action.emoji } : {}),
    },
  };
}
